import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CONTENT } from "@/content";
import { errorText, useApi } from "@/lib/api";
import { ImageUpload } from "@/components/admin/SettingsForm";

const A = CONTENT.admin;
const F = A.fields;
const EMPTY = {
  title: "", verse_text: "", verse_reference: "", translation: "World English Bible", message: "", image_path: null,
  embed_url: "", category: "", tags: [], publish_date: new Date().toLocaleDateString("en-CA"), status: "draft",
};

const Field = ({ label, children }) => <label className="field"><span className="field-label">{label}</span>{children}</label>;

const EmbedField = ({ value, onChange }) => {
  const api = useApi();
  const [state, setState] = useState({ src: "", error: "" });
  useEffect(() => {
    if (!value.trim()) { setState({ src: "", error: "" }); return; }
    const t = setTimeout(() => api("post", "/admin/embed/parse", { raw: value })
      .then((r) => setState({ src: r.src, error: "" }))
      .catch((e) => setState({ src: "", error: errorText(e) })), 400);
    return () => clearTimeout(t);
  }, [value, api]);
  return (
    <Field label={F.embed_url}>
      <textarea className="nb-input" rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={A.embedHint} data-testid="content-embed_url-input" />
      {state.error && <span className="field-error" data-testid="content-embed-error">{state.error}</span>}
      {state.src && <div className="embed-frame small" data-testid="content-embed-preview"><iframe src={state.src} title={A.embedPreview} allowFullScreen /></div>}
    </Field>
  );
};

export const ContentForm = ({ item, onDone, onCancel }) => {
  const api = useApi();
  const [form, setForm] = useState(item ? { ...EMPTY, ...item } : EMPTY);
  const [tagText, setTagText] = useState((item?.tags || []).join(", "));
  const [opts, setOpts] = useState({ categories: [], tags: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("get", "/admin/categories").then(setOpts).catch(() => {}); }, [api]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { id, slug, embed_src, created_at, updated_at, ...body } = form;
    body.tags = tagText.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      await (item ? api("put", `/admin/content/${item.id}`, body) : api("post", "/admin/content", body));
      toast.success(A.saved);
      onDone();
    } catch (err) { toast.error(errorText(err)); }
    finally { setSaving(false); }
  };

  const text = (k, required = false) => (
    <Field label={F[k]}><input className="nb-input" value={form[k]} onChange={set(k)} required={required} data-testid={`content-${k}-input`} /></Field>
  );
  const area = (k, required = false) => (
    <Field label={F[k]}><textarea className="nb-input" rows={3} value={form[k]} onChange={set(k)} required={required} data-testid={`content-${k}-input`} /></Field>
  );

  return (
    <form className="nb-card content-form" onSubmit={submit} data-testid="content-form">
      <h2 className="admin-h2">{item ? A.editTitle : A.newTitle}</h2>
      {text("title", true)}
      {area("verse_text", true)}
      <div className="form-grid">{text("verse_reference", true)}{text("translation")}</div>
      {area("message")}
      <div className="form-grid">
        <Field label={F.category}>
          <input className="nb-input" list="category-options" value={form.category} onChange={set("category")} placeholder={A.categoryHint} data-testid="content-category-input" />
          <datalist id="category-options">{opts.categories.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label={A.tagsLabel}>
          <input className="nb-input" value={tagText} onChange={(e) => setTagText(e.target.value)} list="tag-options" data-testid="content-tags-input" />
          <datalist id="tag-options">{opts.tags.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
      </div>
      <ImageUpload label={F.image} value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v || null })} testId="content-image" />
      <EmbedField value={form.embed_url} onChange={(v) => setForm({ ...form, embed_url: v })} />
      <div className="form-grid">
        <Field label={F.publish_date}>
          <input type="date" className="nb-input" value={form.publish_date} onChange={set("publish_date")} required data-testid="content-publish-date-input" />
        </Field>
        <Field label={F.status}>
          <select className="nb-input" value={form.status} onChange={set("status")} data-testid="content-status-select">
            <option value="draft">{A.statusDraft}</option>
            <option value="published">{A.statusPublished}</option>
          </select>
        </Field>
      </div>
      <div className="row-actions">
        <button type="submit" className="nb-btn" disabled={saving} data-testid="content-save-button">{saving ? A.saving : A.save}</button>
        <button type="button" className="nb-btn ghost" onClick={onCancel} data-testid="content-cancel-button">{A.cancel}</button>
      </div>
    </form>
  );
};
